import React from 'react';
import { ShortCode } from '@highereducation/wordpress';
import { AvatarGroup } from '@highereducation/hen-ui';

type AvatarGroupShortcodeProps = {
  max?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
};

/**
 * AvatarGroupShortcode - Shortcode wrapper for the AvatarGroup atomic (overlapping
 * avatar cluster with optional "+N" overflow). Children are [hen-ui-avatar] shortcodes.
 *
 * Supported shortcode attributes:
 * - max: cap the visible avatars; the remainder collapses into a "+N" overflow badge
 *
 * @example
 * [hen-ui-avatar-group]
 *   [hen-ui-avatar alt="Rebecca Long" fallback="RL" /]
 *   [hen-ui-avatar alt="Andrew Rice" fallback="AR" /]
 * [/hen-ui-avatar-group]
 *
 * @example With overflow
 * [hen-ui-avatar-group max="3"] … five avatars … [/hen-ui-avatar-group]
 */
export const AvatarGroupShortcode: ShortCode<AvatarGroupShortcodeProps> = {
  balanced: true,
  component: ({ max, children }) => {
    const avatars = React.Children.toArray(children).filter(
      React.isValidElement,
    );
    if (avatars.length === 0) return null;

    const parsedMax = max ? Number.parseInt(max, 10) : undefined;
    const maxProp =
      typeof parsedMax === 'number' &&
      Number.isFinite(parsedMax) &&
      parsedMax > 0
        ? parsedMax
        : undefined;

    return <AvatarGroup max={maxProp}>{avatars}</AvatarGroup>;
  },
};
